import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { cn } from '../../lib/utils';
import { SeletorStatus } from './index';
import SeletorPlayStore from './SeletorPlayStore';
import ModalPassos, { PassoModal } from './ModalPassos';
import { AutoCompleteMultiplo, buscarPaginado } from './AutoComplete';

/* GRUPOS PAGINADOS, SO NA LUPA (21/10/2026). O modal baixava TODOS os grupos
   (rows=9999) so por abrir, mesmo que ninguem fosse vincular nada. Mesmo
   endpoint e filtros; SEM `regional=37` (10/09/2026): sem o parametro o backend
   aplica o escopo do usuario logado, como nas outras telas de grupo. */
const buscarGrupos = buscarPaginado(
  (termo, pagina, linhas) =>
    `/entidades/paginadoPrincipal?page=${pagina}&rows=${linhas}&valor=${encodeURIComponent(termo)}` +
    `&campo=nome&direcao=asc&ordenar=nome&status=1`,
  (g) => ({ codigo: g.codigo, rotulo: g.nome })
);

/* ============================================================================
   Modal "Novo aplicativo" - padrao visual novo.

   Decisao registrada: MODAL para CRIAR, PAGINA para EDITAR. Criar nao tem
   registro a linkar; editar tem, e `/dashboard/aplicativos/new/:pacote` e link
   que o suporte manda para alguem - vira modal so quando a URL acompanhar.

   Contratos conferidos no controller (nao assumidos):
     POST /aplicativos                      -> cadastro
     GET  /entidades/paginadoPrincipal?...  -> grupos para vincular
     GET  /aplicativos/paginado?...         -> checagem de pacote ja cadastrado

   A aba "Play Store" fica desabilitada de proposito: a busca no managed Play no
   projeto hoje passa pelo `google-play-scraper`, que e biblioteca de servidor
   (usa Node), e por isso a tela antiga so funciona parcialmente. Colocar um campo
   que nao busca seria pior que dizer que ainda nao esta pronto - fica para quando
   houver endpoint de busca na API.
   ============================================================================ */

type Grupo = { codigo: number; nome: string };

export default function ModalNovoAplicativo({
  aberto,
  onFechar,
  onCriado,
}: {
  aberto: boolean;
  onFechar: () => void;
  onCriado?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [nome, setNome] = useState('');
  const [pacote, setPacote] = useState('');
  const [status, setStatus] = useState(1);
  const [selecionados, setSelecionados] = useState<Grupo[]>([]);
  const [pacoteEmUso, setPacoteEmUso] = useState<boolean | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [lojaAberta, setLojaAberta] = useState(false);

  /* PACOTE VEIO DA LOJA DO GOOGLE?

     Muda duas coisas. O campo fica somente-leitura, porque o valor veio de uma
     fonte autoritativa e digitar em cima dele so pode piorar - um caractere
     trocado cria um registro que nunca aparece em aparelho nenhum, sem erro. E a
     trava de duplicado deixa de impedir o salvamento: quem decide isso passa a ser
     o backend, que recusa com "Ja existe esse aplicativo registrado" em
     aplicativos.service.ts:680. A mensagem continua na tela, como aviso.

     Digitado a mao, a trava do front continua valendo - ali um erro de digitacao e
     provavel, e vale barrar antes de chegar ao servidor. */
  const [pacoteDaLoja, setPacoteDaLoja] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setNome('');
    setPacote('');
    setStatus(1);
    setSelecionados([]);
    setPacoteEmUso(null);
    setPacoteDaLoja(false);
    setErro('');
  }, [aberto]);

  // Validacao de pacote ao vivo, com atraso para nao consultar a cada tecla.
  useEffect(() => {
    if (!pacote.includes('.')) {
      setPacoteEmUso(null);
      return;
    }
    /* IGUALDADE EXATA (30/09/2026). A busca do servidor e `LIKE '%valor%'`:
       contar resultados marcava `br.com.gps` como "ja cadastrado" por existir
       `br.com.gpssa.app`, e travava o salvar. E sem `vivo`, a resposta de um
       texto anterior podia chegar depois e valer para o texto atual. */
    let vivo = true;
    const alvo = pacote.trim();
    setPacoteEmUso(null);
    const t = setTimeout(() => {
      axios
        .get<{ content: Array<{ packageName: string }> }>(
          `/aplicativos/paginado?page=0&rows=50&valor=${encodeURIComponent(alvo)}&campo=packageName&direcao=asc&ordenar=name&status=-1`
        )
        .then((r) => vivo && setPacoteEmUso((r.data?.content ?? []).some((a) => a.packageName === alvo)))
        .catch(() => vivo && setPacoteEmUso(null));
    }, 400);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [pacote]);

  /* GRUPO DEIXOU DE SER OBRIGATORIO (10/09/2026, a pedido).

     Ele travava o salvar com o argumento de que app sem vinculo nao chega a
     aparelho nenhum. O argumento continua verdadeiro - e por isso a aba avisa -
     mas nao justifica a trava: catalogar o aplicativo e distribui-lo sao duas
     decisoes, e frequentemente de pessoas diferentes em momentos diferentes.
     Quem cadastra o pacote hoje pode nao saber ainda quais grupos vao recebe-lo.
     O vinculo continua disponivel aqui e tambem depois, na edicao.

     Vindo da loja, `pacoteEmUso` nao entra na conta - ver o comentario de
     `pacoteDaLoja`. Digitado, ele precisa ser explicitamente `false`: `null`
     significa "ainda nao consultei", e deixar salvar nesse estado seria salvar sem
     ter checado. */
  const podeSalvar =
    nome.trim().length > 0 &&
    pacote.includes('.') &&
    (pacoteDaLoja || pacoteEmUso === false);

  async function salvar() {
    if (!podeSalvar || salvando) return;
    setSalvando(true);
    setErro('');
    try {
      await axios.post('/aplicativos', {
        name: nome.trim(),
        packageName: pacote.trim(),
        status,
      });

      /* GRUPOS ESCOLHIDOS ERAM IGNORADOS (30/09/2026). O corpo levava
         `entidades`, que o POST /aplicativos nunca leu: o app nascia sem vinculo
         nenhum com a tela dizendo "N selecionado(s)". Vincula pela mesma rota do
         botao "Adicionar" da edicao - que regrava a politica de cada grupo. */
      if (selecionados.length) {
        const lista = selecionados.map((g) => ({ codigo: g.codigo, nome: g.nome }));
        try {
          await axios.post('/aplicativos/adicionarAPlicativoNoGrupo', { lista, packageName: pacote.trim() });
          enqueueSnackbar(`Aplicativo cadastrado e vinculado a ${lista.length} grupo(s).`, { variant: 'success' });
        } catch (e: any) {
          enqueueSnackbar(
            `Aplicativo cadastrado, mas não foi possível vincular aos grupos: ${
              e?.response?.data?.message || 'erro desconhecido'
            }. Vincule pela edição do aplicativo.`,
            { variant: 'warning', style: { whiteSpace: 'pre-line' } }
          );
        }
      } else {
        enqueueSnackbar('Aplicativo cadastrado.', { variant: 'success' });
      }
      onCriado?.();
      onFechar();
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível cadastrar o aplicativo.');
    } finally {
      setSalvando(false);
    }
  }

  if (!aberto) return null;

  /* Cada aba responde por uma pergunta, e a validacao acompanha: sem isso o
     rodape diria "tudo preenchido" com um cadastro que o backend recusa. */
  const identificacaoOk = nome.trim().length > 0 && pacote.includes('.') && (pacoteDaLoja || pacoteEmUso === false);
  const gruposOk = selecionados.length > 0;

  const passos: PassoModal[] = [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      grupo: 'Cadastro',
      valido: identificacaoOk,
      pendencia: !nome.trim() ? 'nome' : 'pacote válido',
      resumo: nome.trim() || 'sem nome',
      conteudo: (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold">Nome</span>
              <input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="h-9 w-full rounded-lg border border-line bg-surface px-3 text-[12.5px] outline-none focus:border-primary"
              />
            </label>

            {/* <div> E NAO <label> (28/09/2026).

                Este campo tem DOIS BOTOES ("Escolher na loja" e "Limpar") ANTES
                do <input> no DOM. Um <label> sem htmlFor ativa o primeiro
                descendente rotulavel ao clicar em qualquer area dele - inclusive
                na palavra "Pacote" e no espaco vazio da linha.

                Com o pacote vindo da loja, o primeiro era "Limpar": clicar no
                rotulo ESVAZIAVA o campo e destravava a digitacao, desfazendo a
                escolha que o proprio modal fez questao de travar. Sem pacote da
                loja, o primeiro e "Escolher na loja", e clicar no rotulo abria o
                modal da loja sozinho.

                Mesma causa do defeito do seletor de grupos; ver `Campo` em
                Modal.tsx. Aqui o <label> e cru, entao a troca e direta. */}
            <div className="block">
              <span className="mb-1 flex items-center justify-between gap-2 text-[12px] font-semibold">
                Pacote
                {/* ESCOLHER NA LOJA em vez de digitar.

                    Digitar pacote a mao e a origem de um defeito silencioso: um
                    caractere errado cria um registro que nunca aparece em aparelho
                    nenhum, e nada acusa - para o nosso banco, pacote e so texto.
                    Aqui o proprio Google devolve o pacote do app escolhido.

                    O botao fica NO ROTULO do campo, e nao ao lado do input: ele nao
                    e uma acao do formulario (nao salva, nao valida), e sim outra
                    forma de PREENCHER aquele campo especifico. */}
                <span className="flex items-center gap-2">
                  {/* LIMPAR, e nao "editar a mao" (10/09/2026, a pedido).

                      O botao antigo destravava o campo MANTENDO o texto que veio
                      do Google. Isso abria o pior caminho possivel: alguem escolhe
                      o app certo na loja, destrava, muda um caractere e salva um
                      pacote que nao existe em lugar nenhum - com a aparencia de
                      ter vindo do Google. Pacote e chave de instalacao, nao texto
                      livre; um caractere errado cria um registro que nunca chega a
                      aparelho nenhum e nada acusa.

                      Limpar esvazia e destrava ao mesmo tempo. As duas origens
                      continuam existindo e ficam separadas: escolhido na loja, o
                      valor e do Google e nao se edita; digitado do zero, e livre e
                      passa pela trava de duplicado. O que deixou de existir e o
                      estado hibrido - texto do Google editado a mao. */}
                  {pacoteDaLoja && (
                    <button
                      type="button"
                      onClick={() => {
                        setPacote('');
                        setPacoteDaLoja(false);
                      }}
                      className="inline-flex h-6 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-muted-foreground hover:text-danger"
                      title="Esvazia o campo e libera a digitação"
                    >
                      <svg width="11" height="11" viewBox="0 0 18 18" fill="none" aria-hidden>
                        <path
                          d="M3.5 5h11 M7 5V3.5h4V5 M5 5l.7 9.5h6.6L13 5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Limpar
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setLojaAberta(true)}
                    className="inline-flex h-6 items-center gap-1.5 rounded-md border border-line px-2 text-[11px] font-semibold text-primary hover:border-primary"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M4 4l14 8-14 8V4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                    </svg>
                    {pacoteDaLoja ? 'Trocar na loja' : 'Escolher na loja'}
                  </button>
                </span>
              </span>
              <input
                value={pacote}
                onChange={(e) => setPacote(e.target.value)}
                readOnly={pacoteDaLoja}
                placeholder="com.exemplo.app"
                title={pacoteDaLoja ? 'Preenchido pela loja do Google - use "Limpar" para digitar outro' : undefined}
                /* Travado tem que PARECER travado: fundo de superficie secundaria
                   e cursor de nao-permitido. Campo que ignora a digitacao sem dar
                   sinal nenhum e lido como tela quebrada. */
                className={cn(
                  'h-9 w-full rounded-lg border border-line px-3 tabular-nums text-[12.5px] outline-none focus:border-primary',
                  pacoteDaLoja ? 'cursor-not-allowed bg-surface-2 text-muted-foreground' : 'bg-surface'
                )}
              />
              {/* A mensagem muda de PESO conforme a origem do pacote.

                  Digitado + duplicado = impedimento (vermelho): o salvar fica
                  desabilitado e a frase explica por que.

                  Da loja + duplicado = aviso (amarelo): o app existe mesmo, mas o
                  valor esta correto e quem recusa e o backend. Pintar de vermelho
                  algo que nao bloqueia ensina a ignorar vermelho. */}
              <span
                className={cn(
                  'mt-1 block text-[11px]',
                  pacoteEmUso === true
                    ? pacoteDaLoja
                      ? 'text-warn'
                      : 'text-danger'
                    : pacoteEmUso === false
                      ? 'text-ok'
                      : 'text-faint'
                )}
              >
                {pacoteEmUso === true
                  ? pacoteDaLoja
                    ? 'Este aplicativo já consta na base. Se salvar, o servidor vai recusar.'
                    : 'Este pacote já está cadastrado na base.'
                  : pacoteEmUso === false
                    ? pacoteDaLoja
                      ? 'Pacote vindo da loja do Google, inédito na base.'
                      : 'Pacote válido e inédito na base.'
                    : 'O pacote precisa ter o formato com.exemplo.app'}
              </span>
            </div>
          </div>
        </>
      ),
    },
    {
      id: 'grupos',
      titulo: 'Grupos',
      grupo: 'Cadastro',
      /* `opcional` e nao `valido`: a trilha para de cobrar e o rodape para de
         dizer que falta algo, mas o passo continua contando o que foi escolhido.
         Mesmo tratamento que o passo de grupos ja tinha na EDICAO do aplicativo -
         era so na criacao que ele travava. */
      opcional: true,
      resumo: gruposOk ? `${selecionados.length} selecionado(s)` : 'nenhum',
      conteudo: (
        <>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[12px] font-semibold">
                Vincular a grupos{' '}
                <span className="ml-1 rounded bg-soft px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                  OPCIONAL
                </span>
              </span>
              {/* Sem grupo nao e erro, entao nao e vermelho - mas tambem nao e
                  silencio: a consequencia (ninguem recebe o app) tem que estar
                  escrita, senao a pessoa descobre isso dias depois. */}
              <span className="text-[11px] text-muted-foreground">
                {selecionados.length
                  ? `${selecionados.length} selecionado(s)`
                  : 'nenhum — o app fica catalogado, sem chegar a aparelhos'}
              </span>
            </div>

            {/* Era um filtro mais a lista INTEIRA em caixas de marcar. Com ~800
                grupos, achar um exigia rolar um paredao de caixinhas, e o que
                estava escolhido se perdia no meio das opcoes. */}
            <AutoCompleteMultiplo
              selecionados={selecionados.map((g) => ({ codigo: g.codigo, rotulo: g.nome }))}
              aoMudar={(l) => setSelecionados(l.map((o) => ({ codigo: Number(o.codigo), nome: o.rotulo })))}
              buscar={buscarGrupos}
              // Mesmo teto de 10 grupos por vez da API (adicionarAPlicativoNoGrupo).
              maximo={10}
              titulo="Selecionar grupos"
              placeholder="Nome do grupo (Enter ou lupa)"
              vazio="Nenhum grupo encontrado."
            />
          </div>
          <div>
            <span className="mb-1 block text-[12px] font-semibold">Status inicial</span>
            {/* 🔴 DEFEITO CORRIGIDO - "nao mostrar" qual status esta escolhido.
                Aqui havia uma TERCEIRA copia do seletor de status, e a copia marcava
                o escolhido so com `bg-surface` + sombra (branco sobre branco). Junto
                com `disabled:opacity-40` enquanto nenhum grupo esta escolhido, o
                resultado na tela eram dois botoes cinzas iguais - ninguem consegue
                dizer se o app vai nascer ativo ou inativo.

                Agora e o `SeletorStatus` do sistema, que pinta o escolhido de verde
                ou vermelho e continua legivel desabilitado. Ele fala em texto
                ('ATIVO'/'INATIVO') e este modal guarda numero (o POST manda 1|0),
                dai a conversao nas duas pontas - o contrato com a API nao muda. */}
            {/* Deixou de depender do grupo junto com a obrigatoriedade. O
                travamento existia como reforco da regra antiga; sem a regra, ele
                so escondia um campo que sempre pode ser respondido. */}
            <SeletorStatus
              valor={status === 1 ? 'ATIVO' : 'INATIVO'}
              onChange={(v) => setStatus(v === 'ATIVO' ? 1 : 0)}
            />
          </div>
        </>
      ),
    },

    /* HISTORICO TAMBEM NA CRIACAO (10/09/2026, a pedido).

       Nao ha o que mostrar antes de salvar - e justamente por isso o passo
       precisa existir. Os modais de usuario, grupo, APK e regional ja traziam a
       aba com essa mesma explicacao desde a criacao; o de aplicativo era o unico
       sem ela, e quem cadastrava um app ficava sem saber que aquele cadastro
       passa a ser auditado. A aba responde a pergunta antes de ela ser feita.

       `informativo` mantem o passo fora da conta do rodape: ele nao tem o que
       validar. */
    {
      id: 'historico',
      titulo: 'Histórico',
      grupo: 'Ferramentas',
      informativo: true,
      conteudo: (
        <div className="rounded-lg border border-line bg-surface-2 p-4 text-[12px] text-muted-foreground">
          O histórico começa a ser gravado quando este cadastro for salvo. Cada alteração
          posterior fica registrada aqui com quem fez, quando e o que mudou.
        </div>
      ),
    },
  ];

  return (
    <>
      {/* PADRONIZADO com os outros cinco modais (10/09/2026, a pedido).

          Este era o unico que montava o proprio dialogo a mao - por isso destoava:
          cabecalho diferente, rodape diferente, e tudo numa tela so, com o vinculo
          de grupos espremido entre o pacote e o status. Agora usa o ModalPassos,
          entao ganha de graca a trilha lateral, o estado no rodape e o mesmo
          cabecalho dos demais - e "Vincular a grupos" tem a propria aba, que era o
          pedido. */}
      <ModalPassos
        aberto={aberto}
        titulo="Novo aplicativo"
        subtitulo="Cadastre o pacote e defina o alcance inicial."
        passos={passos}
        rotuloFinalizar="Cadastrar aplicativo"
        onFinalizar={salvar}
        onFechar={onFechar}
        salvando={salvando}
        erro={erro}
      />

  <SeletorPlayStore
    aberto={lojaAberta}
    onFechar={() => setLojaAberta(false)}
    onEscolher={(pacoteEscolhido, nomeEscolhido) => {
      setPacote(pacoteEscolhido);
      setPacoteDaLoja(true);

      /* O NOME VEM DA LOJA (10/09/2026, a pedido).

         O iframe do Google manda so o `packageName` no `onproductselect` - o
         titulo nao vem junto, e por isso o campo Nome continuava vazio depois
         de escolher. Quem escolhia "Revolut Business" na loja ainda tinha que
         digitar o nome, com risco de escrever diferente do que o Google mostra
         e o catalogo divergir da loja.

         Entao perguntamos ao Google: `GET /aplicativos/google/:pacote` chama
         `enterprises.applications.get`. So preenche se o campo estiver VAZIO -
         quem ja digitou um nome proprio (e comum: "Ponto - Matriz") nao pode
         te-lo sobrescrito por uma escolha de pacote.

         Falha em silencio de proposito: se a consulta nao responder, o campo
         fica como estava e a pessoa digita. Isto e conveniencia de
         preenchimento; virar erro seria trocar um incomodo por um impedimento. */
      if (nomeEscolhido && !nome.trim()) {
        setNome(nomeEscolhido);
        return;
      }
      if (!nome.trim()) {
        axios
          .get<{ title?: string | null }>(`/aplicativos/google/${encodeURIComponent(pacoteEscolhido)}`)
          .then((r) => {
            const titulo = String(r.data?.title || '').trim();
            /* Confere de novo o estado ATUAL: entre o pedido e a resposta a
               pessoa pode ter comecado a digitar, e sobrescrever o que ela
               escreveu seria pior que nao preencher. */
            setNome((atual) => (atual.trim() ? atual : titulo));
          })
          .catch(() => {
            /* silencio proposital - ver o comentario acima */
          });
      }
    }}
  />
    </>
  );
}
